import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Sparkles, BarChart3, Users, Play, ArrowRight, Crown, Check,
  Wand2, Plus, Loader2, X, Save,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Campaigns() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);  // campaign object being edited

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/campaigns`);
      setList(data.campaigns || []);
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createDraft = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/campaigns`, {
        name: t("camp.draft_name", { date: new Date().toLocaleDateString() }),
        subject: t("camp.draft_subject"),
        status: "DRAFT",
      });
      toast.success(t("camp.draft_created", { name: data.campaign?.name || "Draft" }));
      await load();
      // Iter51 — stay on /campaigns and scroll to the new draft so the user
      // sees it (previously redirected to /kanban which hid the draft).
      setTimeout(() => {
        const id = data.campaign?.id;
        const el = id && document.querySelector(`[data-testid="camp-item-${id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        else document.querySelector('[data-testid="camp-list"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (_e) {
      toast.error(t("camp.draft_failed"));
    } finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        {/* Top trial banner */}
        <div data-testid="camp-trial-banner" className="bg-[#1A1A2E] text-white px-8 py-2 flex items-center justify-between text-[12px]">
          <div className="flex items-center gap-2">
            <Crown className="w-3.5 h-3.5 text-[#FBBF24]" />
            <span>{t("camp.greeting")}</span>
            <span className="opacity-60">·</span>
            <span>{t("camp.trial_left")}</span>
          </div>
          <a href="#billing" className="text-[#FBBF24] hover:underline">{t("camp.view_plans")}</a>
        </div>

        {/* Header */}
        <header
          data-testid="camp-header"
          className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Logo className="w-9 h-9 object-contain" />
            <div>
              <h1 className="text-[16px] font-semibold text-[var(--al-text)]">{t("camp.nav_title")}</h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5">{t("camp.nav_subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LangToggle />
            <ClaireHelpButton
              testid="campaigns"
              question="Como crio uma cadência de outreach multi-canal e uso variáveis dinâmicas no e-mail?"
            />
            <button
              type="button"
              data-testid="camp-create-btn"
              onClick={createDraft}
              disabled={creating}
              className="al-btn-primary inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[12.5px] font-semibold"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {t("camp.cta_primary")}
            </button>
          </div>
        </header>

        <main className="px-8 py-10 max-w-[1100px] mx-auto space-y-12">
          {/* Hero */}
          <section data-testid="camp-hero" className="grid grid-cols-1 md:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#3B82F6] bg-[#EFF6FF] dark:bg-[#1E293B] px-2.5 py-1 rounded-full mb-4">
                <Mail className="w-3 h-3" /> {t("camp.hero_eyebrow")}
              </div>
              <h2 className="text-[34px] sm:text-[40px] font-extrabold text-[var(--al-text)] leading-[1.1] tracking-tight">
                {t("camp.hero_title")}
              </h2>
              <p className="text-[14px] text-[var(--al-text-muted)] mt-4 leading-relaxed max-w-[480px]">
                {t("camp.hero_body")}
              </p>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  data-testid="camp-cta-start"
                  onClick={createDraft}
                  disabled={creating}
                  className="al-btn-primary inline-flex items-center gap-1.5 h-10 px-5 rounded-lg text-[13px] font-semibold disabled:opacity-50"
                >
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {t("camp.cta_primary")}
                </button>
                <a
                  href="#pricing"
                  data-testid="camp-cta-pricing"
                  className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[13px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
                >
                  {t("camp.cta_pricing")}
                </a>
                <span className="text-[11.5px] text-[var(--al-text-muted)]">{t("camp.cta_hint")}</span>
              </div>
            </div>

            {/* Hero illustration: stylized email card */}
            <div className="relative">
              <div className="al-card p-5 rounded-2xl shadow-xl">
                <div className="flex items-center gap-2 pb-3 border-b border-[var(--al-border)]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
                  <span className="ml-2 text-[10.5px] text-[var(--al-text-muted)] truncate">campaign.draft@agentelead.io</span>
                </div>
                <div className="mt-4 space-y-2.5">
                  <div className="h-2.5 w-3/5 rounded bg-[#3B82F6]/30" />
                  <div className="h-2 w-full rounded bg-[var(--al-border)]" />
                  <div className="h-2 w-11/12 rounded bg-[var(--al-border)]" />
                  <div className="h-2 w-9/12 rounded bg-[var(--al-border)]" />
                  <div className="mt-3 inline-flex items-center gap-1 px-3 h-7 rounded bg-[#3B82F6] text-white text-[11px] font-semibold">
                    {t("camp.hero_button_label")}
                  </div>
                </div>
              </div>
              <div className="absolute -right-3 -top-3 w-12 h-12 rounded-full bg-[#A855F7]/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#A855F7]" />
              </div>
              <div className="absolute -left-4 -bottom-4 w-14 h-14 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-[#22C55E]" />
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section data-testid="camp-benefits" className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Benefit
              icon={Wand2}
              color="#3B82F6"
              title={t("camp.b1_title")}
              body={t("camp.b1_body")}
            />
            <Benefit
              icon={Users}
              color="#A855F7"
              title={t("camp.b2_title")}
              body={t("camp.b2_body")}
            />
            <Benefit
              icon={BarChart3}
              color="#22C55E"
              title={t("camp.b3_title")}
              body={t("camp.b3_body")}
            />
          </section>

          {/* Video / GIF mockup */}
          <section data-testid="camp-video" className="al-card p-0 overflow-hidden">
            <div className="aspect-video bg-gradient-to-br from-[#1E293B] to-[#0F172A] relative flex items-center justify-center">
              <button
                type="button"
                data-testid="camp-video-play"
                onClick={() => toast.info(t("camp.video_demo"))}
                className="w-16 h-16 rounded-full bg-white/95 hover:bg-white text-[#0F172A] flex items-center justify-center transition-transform hover:scale-105 shadow-2xl"
              >
                <Play className="w-7 h-7 ml-1" />
              </button>
              <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-black/60 text-white text-[11px] font-mono">
                00:39
              </div>
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/10 text-white text-[10.5px] font-medium backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />
                {t("camp.video_label")}
              </div>
            </div>
          </section>

          {/* Even more reasons */}
          <section data-testid="camp-reasons" className="al-card p-7">
            <h3 className="text-[20px] font-bold text-[var(--al-text)]">{t("camp.reasons_title")}</h3>
            <p className="text-[13px] text-[var(--al-text-muted)] mt-1">{t("camp.reasons_subtitle")}</p>
            <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3.5">
              {["camp.r1", "camp.r2", "camp.r3", "camp.r4"].map((k) => (
                <li key={k} className="flex items-start gap-2.5 text-[13px] text-[var(--al-text)]">
                  <span className="w-5 h-5 rounded-full bg-[#22C55E]/15 text-[#16A34A] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span className="leading-snug">{t(k)}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Drafts list (real backend data) */}
          <section data-testid="camp-list" className="al-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-[15px] font-bold text-[var(--al-text)]">{t("camp.list_title")}</h3>
                <p className="text-[12px] text-[var(--al-text-muted)] mt-0.5">{t("camp.list_subtitle")}</p>
              </div>
              <span className="text-[11.5px] text-[var(--al-text-muted)]">{list.length} {t("camp.count_label")}</span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-[#3B82F6]" />
              </div>
            ) : list.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-8 h-8 text-[var(--al-text-muted)] mx-auto" />
                <p className="text-[13px] text-[var(--al-text-muted)] mt-2">{t("camp.empty")}</p>
              </div>
            ) : (
              <ul className="divide-y divide-[var(--al-border)]">
                {list.map((c) => (
                  <li key={c.id} data-testid={`camp-item-${c.id}`} className="py-3">
                    <div className="flex items-start justify-between gap-3 mb-1.5">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-bold text-[var(--al-text)] truncate">{c.name}</div>
                        <div className="text-[12px] text-[var(--al-text-muted)] truncate mt-0.5">
                          <span className="font-semibold">{t("camp.col_subject") || "Subject"}:</span> {c.subject || <em>—</em>}
                        </div>
                        {c.body && <div className="text-[11.5px] text-[var(--al-text-muted)] line-clamp-2 mt-1 italic">"{c.body.slice(0, 200)}"</div>}
                        <div className="text-[10.5px] text-[var(--al-text-muted)] mt-1.5 flex flex-wrap gap-3">
                          {c.created_at && <span>📅 {new Date(c.created_at).toLocaleString()}</span>}
                          {typeof c.recipients_count === "number" && <span>👥 {c.recipients_count} {t("camp.col_recipients") || "recipients"}</span>}
                          {typeof c.sent_count === "number" && c.sent_count > 0 && <span>✅ {c.sent_count} sent</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded ${c.status === "DRAFT" ? "bg-[#FEF3C7] text-[#B45309]" : c.status === "ACTIVE" ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#E5E7EB] text-[#6B7280]"}`}>{c.status}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button"
                        data-testid={`camp-item-${c.id}-edit`}
                        onClick={() => setEditing(c)}
                        className="h-7 px-2.5 rounded-md border border-[var(--al-border)] text-[11px] font-semibold hover:bg-[var(--al-row-alt)]">
                        ✏️ {t("camp.action_edit") || "Edit"}
                      </button>
                      <button type="button"
                        data-testid={`camp-item-${c.id}-assign`}
                        onClick={() => { try { window.localStorage.setItem("alp.camp.assign_id", c.id); } catch (_e) { /* ignore */ } navigate("/kanban"); }}
                        className="h-7 px-2.5 rounded-md border border-[#3B82F6] text-[#3B82F6] text-[11px] font-semibold hover:bg-[#EFF6FF]">
                        👥 {t("camp.action_assign") || "Assign leads"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Final CTA */}
          <section className="al-card p-8 text-center bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white border-[#1D4ED8]">
            <h3 className="text-[22px] font-bold">{t("camp.final_title")}</h3>
            <p className="text-[13px] text-white/80 mt-1.5 max-w-[460px] mx-auto">{t("camp.final_body")}</p>
            <button
              type="button"
              data-testid="camp-final-cta"
              onClick={createDraft}
              disabled={creating}
              className="mt-5 inline-flex items-center gap-1.5 h-10 px-6 rounded-lg bg-white text-[#1D4ED8] text-[13px] font-bold hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {t("camp.cta_primary")}
            </button>
          </section>
        </main>
      </div>
      {editing && (
        <CampaignEditModal
          campaign={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          isPt={t("camp.action_edit") === "Editar"}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Iter53 — inline campaign editor modal. Edits name, subject, body, status.

function CampaignEditModal({ campaign, onClose, onSaved, isPt }) {
  const [name, setName] = useState(campaign.name || "");
  const [subject, setSubject] = useState(campaign.subject || "");
  const [body, setBody] = useState(campaign.body || "");
  const [status, setStatus] = useState(campaign.status || "DRAFT");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.patch(`${API}/campaigns/${campaign.id}`, {
        name: name.trim(),
        subject: subject.trim(),
        body,
        status,
      });
      toast.success(isPt ? "Campanha atualizada" : "Campaign updated");
      onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || (isPt ? "Falha ao salvar" : "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-4" onClick={onClose}>
      <form data-testid="camp-edit-modal" onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900 border border-[var(--al-border)] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-[20px] font-extrabold inline-flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#3B82F6]" />
            {isPt ? "Editar campanha" : "Edit campaign"}
          </h3>
          <button type="button" onClick={onClose} className="text-[var(--al-text-muted)] hover:text-[var(--al-text)] p-1"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{isPt ? "Nome" : "Name"}</div>
            <input value={name} onChange={(e) => setName(e.target.value)} required
              data-testid="camp-edit-name"
              className="w-full al-input h-10 text-[13px]" />
          </label>
          <label>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">Status</div>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              data-testid="camp-edit-status"
              className="w-full al-input h-10 text-[13px]">
              <option value="DRAFT">DRAFT</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="PAUSED">PAUSED</option>
            </select>
          </label>
          <label className="col-span-2">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{isPt ? "Assunto" : "Subject"}</div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid="camp-edit-subject"
              className="w-full al-input h-10 text-[13px]" />
          </label>
          <label className="col-span-2">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{isPt ? "Corpo do e-mail" : "Email body"}</div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9}
              data-testid="camp-edit-body"
              className="w-full al-input p-3 text-[12.5px] font-mono leading-relaxed" />
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-lg text-[var(--al-text-muted)] hover:bg-[var(--al-row-alt)] text-[12.5px] font-semibold">
            {isPt ? "Cancelar" : "Cancel"}
          </button>
          <button type="submit" disabled={busy}
            data-testid="camp-edit-submit"
            className="h-10 px-5 rounded-lg bg-[#3B82F6] hover:bg-[#1D4ED8] text-white font-bold disabled:opacity-50 inline-flex items-center gap-2 text-[12.5px]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isPt ? "Salvar" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Benefit({ icon: Icon, color, title, body }) {
  return (
    <div className="al-card p-6">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
        style={{ background: `${color}1A`, color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <h4 className="text-[15px] font-bold text-[var(--al-text)]">{title}</h4>
      <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1.5 leading-relaxed">{body}</p>
    </div>
  );
}
